export const ProductionMachineType = (json: IProductionMachineType): IProductionMachineTypeModel => {
	const { is_active, created_at, updated_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const ProductionMachineChangeover = (json: IProductionMachineChangeover): IProductionMachineChangeoverModel => {
	const { created_at, updated_at, short_name, production_machine_id, is_active, ...rest } = json

	return {
		...rest,
		shortName: short_name,
		productionMachineId: production_machine_id,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const ProductionMachinePerformanceParameter = (
	json: IProductionMachinePerformanceParameter,
): IProductionMachinePerformanceParameterModel => {
	const { is_active, product_type_id, created_at, updated_at, ...rest } = json

	return {
		...rest,
		isActive: is_active,
		productTypeId: product_type_id ?? null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const ProductionMachineQualityControlParameter = (
	json: IProductionMachineQualityControlParameter,
): IProductionMachineQualityControlParameterModel => {
	const { created_at, updated_at, production_machine_id, product_type_id, ...rest } = json

	return {
		...rest,
		productionMachineId: production_machine_id,
		productTypeId: product_type_id ?? null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

const ProductionMachineCapacityParameter = (
	json: IProductionMachineCapacityParameter,
): IProductionMachineCapacityParameterModel => {
	const { created_at, updated_at, production_machine_id, product_type_id, ...rest } = json

	return {
		...rest,
		productionMachineId: production_machine_id,
		productTypeId: product_type_id ?? null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const ProductionMachine = (json: IProductionMachine): IProductionMachineModel => {
	const {
		is_active,
		created_at,
		updated_at,
		short_name,
		type,
		machine_type_id,
		additional_parameters,
		machine_changeovers,
		performance_parameters,
		quality_control_parameters,
		capacity_parameters,
		...rest
	} = json

	return {
		...rest,
		isActive: is_active,
		shortName: short_name,
		type: ProductionMachineType(type),
		machineTypeId: machine_type_id,
		additionalParameters: additional_parameters ? additional_parameters.map(ProductionAdditionalParameter) : [],
		machineChangeovers: machine_changeovers ? machine_changeovers.map(ProductionMachineChangeover) : [],
		performanceParameters: performance_parameters
			? performance_parameters.map(ProductionMachinePerformanceParameter)
			: [],
		qualityControlParameters: quality_control_parameters
			? quality_control_parameters.map(ProductionMachineQualityControlParameter)
			: [],
		capacityParameters: capacity_parameters ? capacity_parameters.map(ProductionMachineCapacityParameter) : [],
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const ProductionMachineModelToParametersPayload = (
	model: IProductionMachineParameterModelForm,
): IProductionMachineModelToParametersPayload => {
	const { productTypeId, performanceParameters, additionalParameters, qualityControlParameters, capacityParameters } =
		model

	return {
		performance_parameters: performanceParameters.map(param =>
			ProductionPerformanceParameterToPayload(productTypeId, param),
		),
		additional_parameters: additionalParameters.map(param =>
			ProductionAdditionalParameterToPayload(productTypeId, param),
		),
		quality_control_parameters: qualityControlParameters.map(param =>
			ProductionQualityControlParameterToPayload(productTypeId, param),
		),
		capacity_parameters: capacityParameters.map(param =>
			ProductionCapacityParameterToPayload(productTypeId, param),
		),
	}
}

const ProductionMachineChangeoverPayload = (
	changeover: IProductionMachineChangeoverModel,
): IProductionMachineChangeoverPayload => {
	const { id, name, shortName, description, time, instruction, triggers } = changeover

	return {
		id,
		name,
		description,
		instruction,
		triggers,
		time,
		short_name: shortName,
	}
}

export const ProductionMachineToCreatePayload = (
	event: IFormSubmitEvent<IProductionMachineEventForm>,
	options: IProductionMachineModelToPayloadOptions,
): IProductionMachineCreatePayload => {
	const { name, description, shortName, machineTypeId } = event.values

	const parameters = options.models.map(ProductionMachineModelToParametersPayload)

	return {
		name,
		description,
		short_name: shortName,
		machine_type_id: machineTypeId,
		performance_parameters: Object.values(parameters).flatMap(o => o.performance_parameters),
		additional_parameters: Object.values(parameters).flatMap(o => o.additional_parameters),
		quality_control_parameters: Object.values(parameters).flatMap(o => o.quality_control_parameters),
		capacity_parameters: Object.values(parameters).flatMap(o => o.capacity_parameters),
		machine_changeovers: options.changeovers.map(ProductionMachineChangeoverPayload),
	}
}

export const ProductionMachineModelToUpdatePayload = (
	updated: IProductionMachineModel,
	event: IFormSubmitEvent<IProductionMachineEventForm>,
	options: IProductionMachineModelToPayloadOptions,
): IProductionMachineUpdatePayload => {
	const { name, description, shortName, machineTypeId } = event.values

	const parameters = options.models.map(ProductionMachineModelToParametersPayload)

	return {
		name,
		description,
		id: updated.id,
		short_name: shortName,
		machine_type_id: machineTypeId,
		performance_parameters: Object.values(parameters).flatMap(o => o.performance_parameters),
		additional_parameters: Object.values(parameters).flatMap(o => o.additional_parameters),
		quality_control_parameters: Object.values(parameters).flatMap(o => o.quality_control_parameters),
		capacity_parameters: Object.values(parameters).flatMap(o => o.capacity_parameters),
		machine_changeovers: options.changeovers.map(ProductionMachineChangeoverPayload),
	}
}
